import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRightCircle, Trash2 } from 'lucide-react';
import {
  adminConvertInquiryToOrder,
  adminDeleteInquiry,
  adminGetInquiry,
  adminUpdateInquiry,
  describeApiError,
} from '../../services/api';
import type { Inquiry, InquiryStatus, OrderLineInput } from '../../types';
import { ITEM_SOURCE_LABEL } from './orderUtils';
import OrderLinesEditor from '../../components/admin/OrderLinesEditor';
import { useConfirm } from '../../hooks/useConfirm';
import ImageLightbox from '../../components/ImageLightbox';
import { formatPhone, phoneHref } from '../../utils/contact';

const INQUIRY_STATUS_LABEL: Record<InquiryStatus, string> = {
  new: 'New',
  reviewed: 'Reviewed',
  quoted: 'Quoted',
  declined: 'Declined',
  converted: 'Converted',
};

const InquiryDetail: React.FC = () => {
  const { inquiryId } = useParams<{ inquiryId: string }>();
  const navigate = useNavigate();
  const [inquiry, setInquiry] = useState<Inquiry | undefined>();
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<InquiryStatus>('new');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [savedHint, setSavedHint] = useState<string | undefined>();
  const { confirm, confirmDialog } = useConfirm();
  // Which reference photo the viewer is showing, if any.
  const [viewing, setViewing] = useState<number | undefined>();

  // Convert-to-order draft
  const [showConvert, setShowConvert] = useState(false);
  const [lines, setLines] = useState<OrderLineInput[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [shippingCents, setShippingCents] = useState(0);
  const [taxCents, setTaxCents] = useState(0);

  useEffect(() => {
    if (!inquiryId) return;
    setLoading(true);
    adminGetInquiry(inquiryId)
      .then(i => {
        setInquiry(i);
        setNotes(i.internal_notes ?? '');
        setStatus(i.status);
        // Seed a first line that copies inquiry info so the admin can edit + price it.
        if (lines.length === 0) {
          setLines([
            {
              product_name: 'Custom order',
              description: i.message,
              qty: 1,
              unit_price_cents: 0,
              item_source: i.item_source,
            },
          ]);
        }
      })
      .catch(e => setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to load'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inquiryId]);

  const flash = (msg: string) => {
    setSavedHint(msg);
    window.setTimeout(() => setSavedHint(undefined), 2000);
  };

  const saveNotes = async () => {
    if (!inquiryId) return;
    setSaving(true);
    try {
      const updated = await adminUpdateInquiry(inquiryId, { internal_notes: notes, status });
      setInquiry(updated);
      flash('Saved');
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!inquiryId || !inquiry) return;
    const photos = inquiry.image_urls?.length ?? 0;
    const ok = await confirm({
      title: `Delete the request from ${inquiry.customer_name}?`,
      body: `This removes the request${
        photos ? ` and its ${photos} reference photo${photos === 1 ? '' : 's'}` : ''
      } for good. ${
        inquiry.converted_order_id
          ? 'The order it became is not affected.'
          : 'This cannot be undone.'
      }`,
      confirmLabel: 'Delete request',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await adminDeleteInquiry(inquiryId);
      navigate('/admin/inquiries');
    } catch (e: any) {
      setError(describeApiError(e, 'Delete failed'));
    }
  };

  const convert = async () => {
    if (!inquiryId || !inquiry) return;
    setSaving(true);
    try {
      const order = await adminConvertInquiryToOrder(inquiryId, {
        customer_name: inquiry.customer_name,
        customer_email: inquiry.customer_email,
        customer_phone: inquiry.customer_phone,
        lines,
        shipping_cents: shippingCents,
        tax_cents: taxCents,
        due_date: dueDate || undefined,
        status: 'created',
      });
      navigate(`/admin/orders/${order.order_id}`);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Convert failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-text-light">Loading…</div>;
  if (!inquiry) return <div className="text-red-600 text-sm">Inquiry not found.</div>;

  return (
    <div className="space-y-6 max-w-3xl">
      {confirmDialog}
      <ImageLightbox
        urls={inquiry.image_urls ?? []}
        index={viewing}
        onIndexChange={setViewing}
        onClose={() => setViewing(undefined)}
        label="Reference photo"
      />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-semibold">Inquiry — {inquiry.customer_name}</h1>
        <div className="flex items-center flex-wrap gap-2">
          {savedHint && <span className="text-sm text-green-700">{savedHint}</span>}
          <button
            type="button"
            onClick={remove}
            className="border border-red-200 text-red-600 rounded px-3 py-2 text-sm hover:bg-red-50 inline-flex items-center gap-1"
          >
            <Trash2 size={14} /> Delete
          </button>
          {inquiry.converted_order_id ? (
            <button
              type="button"
              onClick={() => navigate(`/admin/orders/${inquiry.converted_order_id}`)}
              className="bg-primary text-white rounded px-3 py-2 text-sm font-medium inline-flex items-center gap-1"
            >
              <ArrowRightCircle size={14} /> View order
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowConvert(s => !s)}
              className="bg-primary text-white rounded px-3 py-2 text-sm font-medium inline-flex items-center gap-1"
            >
              <ArrowRightCircle size={14} /> Create order from inquiry
            </button>
          )}
        </div>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <section className="bg-white border border-background-dark rounded p-4 space-y-2">
        <div className="text-sm">
          <span className="text-text-light">From:</span> {inquiry.customer_name} (
          <a href={`mailto:${inquiry.customer_email}`} className="text-primary hover:underline">
            {inquiry.customer_email}
          </a>
          {inquiry.customer_phone && (
            <>
              {' · '}
              {/* Dialable: this page is read on a phone as often as a
                  laptop, and the number is the fastest way to answer a
                  question about a request. */}
              <a
                href={phoneHref(inquiry.customer_phone)}
                className="text-primary hover:underline"
              >
                {formatPhone(inquiry.customer_phone)}
              </a>
            </>
          )}
          )
        </div>
        <div className="text-sm">
          <span className="text-text-light">Item source:</span>{' '}
          {ITEM_SOURCE_LABEL[inquiry.item_source]}
        </div>
        {/* What the customer asked for, so the quote can include postage
            and the address doesn't have to be chased by email. It carries
            over automatically when this becomes an order. */}
        <div className="text-sm">
          <span className="text-text-light">Delivery:</span>{' '}
          {inquiry.fulfillment === 'shipping' ? 'Ship to customer' : 'Local pickup'}
        </div>
        {inquiry.fulfillment === 'shipping' && inquiry.shipping_address && (
          <div className="text-sm text-text-light whitespace-pre-line pl-1 border-l-2 border-background-dark">
            {[
              inquiry.shipping_address.line1,
              inquiry.shipping_address.line2,
              `${inquiry.shipping_address.city}, ${inquiry.shipping_address.state} ${inquiry.shipping_address.postal_code}`,
            ]
              .filter(Boolean)
              .join('\n')}
          </div>
        )}
        <div className="text-sm">
          <span className="text-text-light">Received:</span>{' '}
          {new Date(inquiry.created_at).toLocaleString()}
        </div>
        <div className="border-t border-background-dark pt-3">
          <div className="text-xs text-text-light mb-1">Message</div>
          <div className="text-sm whitespace-pre-line">{inquiry.message}</div>
        </div>

        {/* The reference photos are the point of most custom requests, and
            this page only ever showed the message. Each opens full size —
            quoting from a 130px thumbnail isn't realistic. */}
        <div className="border-t border-background-dark pt-3">
          <div className="text-xs text-text-light mb-2">
            Reference photos{inquiry.image_urls?.length ? ` (${inquiry.image_urls.length})` : ''}
          </div>
          {inquiry.image_urls?.length ? (
            <div className="flex flex-wrap gap-2">
              {inquiry.image_urls.map((url, i) => (
                <button
                  key={url}
                  type="button"
                  onClick={() => setViewing(i)}
                  title="View full size"
                  aria-label={`View reference photo ${i + 1} full size`}
                  className="block cursor-zoom-in"
                >
                  <img
                    src={url}
                    alt={`Reference ${i + 1}`}
                    className="h-32 w-32 object-cover rounded border border-background-dark hover:border-primary transition"
                  />
                </button>
              ))}
            </div>
          ) : (
            <div className="text-sm text-text-light italic">None attached.</div>
          )}
        </div>
      </section>

      <section className="bg-white border border-background-dark rounded p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:items-end">
          <div>
            <label className="block text-xs text-text-light mb-1">Status</label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value as InquiryStatus)}
              className="w-full border border-background-dark rounded px-3 py-2 text-sm bg-white"
            >
              {(Object.keys(INQUIRY_STATUS_LABEL) as InquiryStatus[]).map(s => (
                <option key={s} value={s}>
                  {INQUIRY_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={saveNotes}
            disabled={saving}
            className="bg-primary text-white rounded px-4 py-2 text-sm font-medium disabled:opacity-50 justify-self-end"
          >
            Save
          </button>
        </div>
        <div>
          <label className="block text-xs text-text-light mb-1">Internal notes</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={4}
            className="w-full border border-background-dark rounded px-3 py-2 text-sm"
          />
        </div>
      </section>

      {showConvert && !inquiry.converted_order_id && (
        <section className="bg-white border border-background-dark rounded p-4 space-y-4">
          <div className="text-sm font-medium">Build the order</div>
          <p className="text-xs text-text-light">
            Set up the line items, pricing, and due date. After creating, you can send an
            invoice from the order page.
          </p>
          <OrderLinesEditor lines={lines} onChange={setLines} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-text-light mb-1">Due date</label>
              <input
                type="date"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                className="w-full border border-background-dark rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-text-light mb-1">Shipping ($)</label>
              <input
                type="number"
                step="0.01"
                min={0}
                value={(shippingCents / 100).toFixed(2)}
                onChange={e => setShippingCents(Math.round((Number(e.target.value) || 0) * 100))}
                className="w-full border border-background-dark rounded px-3 py-2 text-sm text-right"
              />
            </div>
            <div>
              <label className="block text-xs text-text-light mb-1">Tax ($)</label>
              <input
                type="number"
                step="0.01"
                min={0}
                value={(taxCents / 100).toFixed(2)}
                onChange={e => setTaxCents(Math.round((Number(e.target.value) || 0) * 100))}
                className="w-full border border-background-dark rounded px-3 py-2 text-sm text-right"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={convert}
              disabled={saving || lines.length === 0}
              className="bg-primary text-white rounded px-4 py-2 text-sm font-medium disabled:opacity-50"
            >
              {saving ? 'Creating…' : 'Create order'}
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default InquiryDetail;
